import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";
import { NovaRealtimeSessionService } from "./nova-realtime-session.service";
import { NovaRealtimeServerEvent, NovaRealtimeState } from "./nova-realtime.model";
import {
  NOVA_REALTIME_EVENT_ALIASES,
  NOVA_REALTIME_EVENTS,
  NOVA_REALTIME_VOICE_VIEW_INITIAL,
  NovaRealtimeVoiceView,
} from "./nova-realtime-voice-ui.model";

/**
 * Matches a complete `[[...]]` tag anywhere in the caption.
 *
 * Belt and braces on C1. The system prompt's `[[NAV:X]]` and `[[SAFETY_ESCALATION_TRIGGERED]]`
 * markers are stripped and acted on upstream, and Nova is instructed not to say them aloud - but
 * on this path the caption is her transcript of her own audio, so anything she DID say arrives
 * here verbatim. A tag that slips through should at least not be shown to the user.
 */
const CAPTION_TAG = /\[\[[^\]]*\]\]/g;

/**
 * A tag that has only half arrived: `[[NAV:Sc` at the end of the buffer, with the rest still in
 * flight. Suppressed rather than shown, so a tag never flashes up for the one frame between its
 * opening brackets and its closing ones.
 */
const CAPTION_TAG_TAIL = /\[\[[^\]]*$/;

/**
 * Removes `[[...]]` tags from a caption, complete or half-arrived.
 *
 * Exported for the suite: this is the guarantee the C1 belt-and-braces line in FLIP-276's DoD
 * asks for, and it is worth testing directly rather than only through a reducer.
 */
export function scrubCaption(text: string): string {
  return text.replace(CAPTION_TAG, "").replace(CAPTION_TAG_TAIL, "");
}

/** Reads a string field off an open server event, or "" when it is absent or the wrong shape. */
function readText(event: NovaRealtimeServerEvent, field: string): string {
  const value = event[field];
  return typeof value === "string" ? value : "";
}

/**
 * FLIP-276. Turns one server event into the next view.
 *
 * A pure function, deliberately: every interesting question about this path - does barge-in show
 * immediately, does the status bar claim Nova is speaking before she is, does a stray tag reach
 * the screen - is answerable by feeding it event objects, with no peer connection, no microphone
 * and no audio. Headless Chrome has none of those.
 *
 * The caption buffers are held unscrubbed in `novaCaptionRaw` so that scrubbing is idempotent over
 * a stream of deltas: scrubbing the accumulated text on each pass, and accumulating the SCRUBBED
 * text, are different things, and only the first can suppress a tag that spans two deltas.
 */
export function reduceRealtimeVoiceEvent(
  previous: NovaRealtimeVoiceView,
  novaCaptionRaw: string,
  event: NovaRealtimeServerEvent
): { view: NovaRealtimeVoiceView; novaCaptionRaw: string } {
  const type = NOVA_REALTIME_EVENT_ALIASES[event.type] ?? event.type;
  const unchanged = { view: previous, novaCaptionRaw };

  switch (type) {
    case NOVA_REALTIME_EVENTS.speechStarted: {
      // Barge-in feedback, the moment the user takes the floor. The server's turn detection has
      // already made the call, so there is nothing to confirm and nothing to wait for - which is
      // the whole reason this is instant here and was a judgement call on the legacy path.
      const interrupting = previous.state === "speaking" || previous.state === "thinking";
      return {
        view: {
          ...previous,
          state: interrupting ? "interrupted" : "listening",
          // Their previous turn's words are not their current one's.
          userCaption: "",
        },
        novaCaptionRaw,
      };
    }

    case NOVA_REALTIME_EVENTS.speechStopped:
      // The floor is Nova's again. Nothing has been generated yet, so this is honestly "thinking"
      // and not "speaking" - the gap the legacy path used to mislabel in both directions.
      return { view: { ...previous, state: "thinking" }, novaCaptionRaw };

    case NOVA_REALTIME_EVENTS.inputTranscriptDelta:
      return {
        view: { ...previous, userCaption: previous.userCaption + readText(event, "delta") },
        novaCaptionRaw,
      };

    case NOVA_REALTIME_EVENTS.inputTranscriptDone: {
      // The final transcript replaces the accumulated deltas rather than appending to them: it is
      // the same words, corrected, and appending would show the sentence twice.
      const transcript = readText(event, "transcript");
      return {
        view: { ...previous, userCaption: transcript || previous.userCaption },
        novaCaptionRaw,
      };
    }

    case NOVA_REALTIME_EVENTS.responseCreated:
      // A new reply. Nova's previous caption belongs to the previous turn.
      //
      // FLIP-284 follow-up. `responseActive` is the signal for "Nova has the floor", and it is
      // deliberately NOT `audioStarted`. A live session showed `output_audio_buffer.stopped`
      // arriving about half a second into a reply that ran for another ten - the buffer drains
      // between chunks - so anything keyed on the audio boolean died mid-sentence. The response
      // lifecycle is the reliable bracket: created here, cleared on `response.done`.
      return {
        view: {
          ...previous,
          state: "thinking",
          novaCaption: "",
          audioStarted: false,
          responseActive: true,
        },
        novaCaptionRaw: "",
      };

    case NOVA_REALTIME_EVENTS.outputTranscriptDelta: {
      const raw = novaCaptionRaw + readText(event, "delta");
      return {
        view: { ...previous, state: "speaking", novaCaption: scrubCaption(raw) },
        novaCaptionRaw: raw,
      };
    }

    case NOVA_REALTIME_EVENTS.outputTranscriptDone: {
      const transcript = readText(event, "transcript");
      const raw = transcript || novaCaptionRaw;
      return {
        view: { ...previous, state: "speaking", novaCaption: scrubCaption(raw) },
        novaCaptionRaw: raw,
      };
    }

    case NOVA_REALTIME_EVENTS.audioBufferStarted:
      // Sound is genuinely coming out. Only now may the status bar say she is speaking.
      return { view: { ...previous, state: "speaking", audioStarted: true }, novaCaptionRaw };

    case NOVA_REALTIME_EVENTS.audioBufferStopped:
      // NOT the end of the reply. The output buffer drains between chunks, so this arrives while
      // she is still talking. Only the audio flag is lowered; who holds the floor is unchanged.
      return { view: { ...previous, audioStarted: false }, novaCaptionRaw };

    case NOVA_REALTIME_EVENTS.audioBufferCleared:
      // Dropped audio is what a barge-in does, so this one genuinely ends the reply.
      return {
        view: { ...previous, state: "listening", audioStarted: false, responseActive: false },
        novaCaptionRaw,
      };

    case NOVA_REALTIME_EVENTS.responseDone:
      // The one reliable end of a reply, and the only place `responseActive` is cleared on a normal
      // turn. `output_audio_buffer.stopped` above no longer ends anything, because it does not
      // mean what its name suggests - see responseCreated.
      return {
        view: { ...previous, state: "listening", audioStarted: false, responseActive: false },
        novaCaptionRaw,
      };

    default:
      return unchanged;
  }
}

/**
 * FLIP-276. The voice UI's view of a Realtime session: mic state, speaking state, barge-in feedback
 * and both captions.
 *
 * WHY THIS IS NOT IN THE COMPONENT. `nova-companion-chat.component.ts` is 3,896 lines and its
 * voice UI is currently welded to acoustic measurement - analyser taps, loudness thresholds, echo
 * ratios and hold windows. Inlining a second, completely different set of transitions beside the
 * first would leave two state machines in one file with no way to tell which one is live. Reducing
 * events here leaves the component doing what a component should: rendering the answer.
 *
 * WHAT THIS SERVICE DOES NOT DO. It does not relay events to our server (FLIP-274), answer tool
 * calls (FLIP-275), or decide which voice path is in use (FLIP-278). It reads `events$` and says
 * what the screen should show.
 */
@Injectable({
  providedIn: "root",
})
export class NovaRealtimeVoiceUiService implements OnDestroy {
  private readonly viewSource = new BehaviorSubject<NovaRealtimeVoiceView>(
    NOVA_REALTIME_VOICE_VIEW_INITIAL
  );

  /**
   * What the voice UI should show. Replays, so a component that opens mid-session - which
   * minimising to the FAB and navigating back makes ordinary - renders the live state rather than
   * an empty bar it has to wait for the next event to correct.
   */
  readonly view$: Observable<NovaRealtimeVoiceView> = this.viewSource.asObservable();

  /**
   * Nova's caption before scrubbing. Kept beside the view rather than in it: the view is what the
   * screen shows, and the unscrubbed text is deliberately not that.
   */
  private novaCaptionRaw = "";

  private readonly subscriptions = new Subscription();

  constructor(private session: NovaRealtimeSessionService) {
    this.subscriptions.add(
      this.session.events$.subscribe((event) => this.onEvent(event))
    );
    this.subscriptions.add(
      this.session.state$.subscribe((state) => this.onTransportState(state))
    );
  }

  /**
   * Nova's inbound audio, for the waveform's analyser.
   *
   * Passed through rather than re-exported, so the component takes one dependency for the whole
   * Realtime voice UI instead of reaching past this service into the transport.
   */
  get remoteStream$(): Observable<MediaStream | null> {
    return this.session.remoteStream$;
  }

  /** The user's microphone, for the listening half of the same waveform. */
  get microphoneStream$(): Observable<MediaStream | null> {
    return this.session.microphoneStream$;
  }

  /** The view right now, for callers that need it without subscribing. */
  get view(): NovaRealtimeVoiceView {
    return this.viewSource.value;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private onEvent(event: NovaRealtimeServerEvent): void {
    const next = reduceRealtimeVoiceEvent(this.viewSource.value, this.novaCaptionRaw, event);
    this.novaCaptionRaw = next.novaCaptionRaw;
    if (next.view !== this.viewSource.value) {
      this.viewSource.next(next.view);
    }
  }

  /**
   * The transport's own state, which brackets everything the events say.
   *
   * `connected` is what turns the UI on: the mic is live and the floor is the user's before
   * anyone has said anything, which is exactly "listening". Everything else is off - a connecting,
   * failed or ending session has no turn to be on either side of, and leaving the bar mid-
   * conversation after the call has dropped is the specific failure FLIP-276 exists to prevent.
   */
  private onTransportState(state: NovaRealtimeState): void {
    if (state === "connected") {
      if (this.viewSource.value.state === "off") {
        this.viewSource.next({ ...NOVA_REALTIME_VOICE_VIEW_INITIAL, state: "listening" });
      }
      return;
    }

    this.novaCaptionRaw = "";
    if (this.viewSource.value.state !== "off") {
      this.viewSource.next(NOVA_REALTIME_VOICE_VIEW_INITIAL);
    }
  }
}
